import _ from 'lodash'

interface ExportInterface {
  branchName: string
  dateStart: string
  dateEnd: string
  receiptPrefix: string
  doctorsFilter: string
  labGroupName: string
  labCompanyName: string
  includeSn: boolean
  data: any
  doctors: any[]
  t: any
}

const fmt = (n: any, digits = 2) => {
  const v = Number(n) || 0
  return v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

const escapeHtml = (s: any): string => {
  if (s === null || s === undefined) return ''
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export const ExportClinicFeeDentistDccPdf = async (rpt: ExportInterface) => {
  const title = rpt.t('REPORT.REPORT_NAME.REMUNERATION_CLINIC_FEE_ALL_DENTIST_DCC')
  const rawRows: any[] = rpt.data?.rawRows || []
  const rowDf: any[] = rpt.data?.rowDf || []
  const summaryIncome = rpt.data?.summaryIncome

  const doctorName = (id: any) =>
    _.get(_.find(rpt.doctors, { dentistId: Number(id) }), 'dentistFullname') || '-'

  const filterRows = [
    [rpt.t('REPORT.FILTER.BRANCH'), rpt.branchName],
    [rpt.t('REPORT.FILTER.DATE_START') || 'วันที่เริ่ม', `${rpt.dateStart || '-'} - ${rpt.dateEnd || '-'}`],
    [rpt.t('REPORT.FILTER.RECEIPT_TYPE'), rpt.receiptPrefix],
    [rpt.t('USER_DOCTOR'), rpt.doctorsFilter],
    [rpt.t('REPORT.FILTER.LAB_GROUP'), rpt.labGroupName],
    [rpt.t('REPORT.FILTER.LAB_COMPANY'), rpt.labCompanyName]
  ]

  const mainHeaders = [
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NO'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.BILL_NO'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DOCTOR'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.PATIENT_CODE'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.PATIENT_NAME'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.REMARK_DISCOUNT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TX'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.MED'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.PD'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.MAT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.ST'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.XRAY'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.AMOUNT_PAID'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CASH'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TRANSFER'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DEBIT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CREDIT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TREATMENT_RIGHT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.ADVANCE')
  ]

  const mainHeaderHtml = mainHeaders.map((h) => `<th>${escapeHtml(h)}</th>`).join('')

  const mainBodyHtml = rawRows
    .map((row: any, i: number) => {
      const cells = [
        i + 1,
        row.billNo || '-',
        doctorName(row.doctorId),
        row.branchCnNumber || row.CnNumber || '-',
        row.patientFullname || '-',
        row.remarkDiscount || '-',
        fmt(row.paidTx),
        fmt(row.cfPaid),
        fmt(row.dfPaid),
        fmt(row.paidMed),
        fmt(row.paidPd),
        fmt(row.paidMat),
        fmt(row.paidSt),
        fmt(row.paidXray),
        fmt(row.amountPaid),
        fmt(row.amountCash),
        fmt(row.amountTransfer),
        fmt(row.amountDebit),
        fmt(row.creditCost),
        fmt(row.amountCredit),
        fmt(row.amountTreatmentRight),
        fmt(row.amountAdvance)
      ]
      const cellHtml = cells
        .map((c, idx) => `<td class="${idx >= 6 ? 'num' : ''}">${escapeHtml(c)}</td>`)
        .join('')
      return `<tr>${cellHtml}</tr>`
    })
    .join('')

  const sumValues = [
    _.sumBy(rawRows, 'paidTx'),
    _.sumBy(rawRows, 'cfPaid'),
    _.sumBy(rawRows, 'dfPaid'),
    _.sumBy(rawRows, 'paidMed'),
    _.sumBy(rawRows, 'paidPd'),
    _.sumBy(rawRows, 'paidMat'),
    _.sumBy(rawRows, 'paidSt'),
    _.sumBy(rawRows, 'paidXray'),
    _.sumBy(rawRows, 'amountPaid'),
    _.sumBy(rawRows, 'amountCash'),
    _.sumBy(rawRows, 'amountTransfer'),
    _.sumBy(rawRows, 'amountDebit'),
    _.sumBy(rawRows, 'creditCost'),
    _.sumBy(rawRows, 'amountCredit'),
    _.sumBy(rawRows, 'amountTreatmentRight'),
    _.sumBy(rawRows, 'amountAdvance')
  ]

  const sumLabel = rpt.t('REPORT.TITLE.SUM')
  const mainSumRowHtml = rawRows.length
    ? `<tr class="row-sum">
        <td colspan="6" class="sum-label">${escapeHtml(sumLabel)}</td>
        ${sumValues.map((v) => `<td class="num">${fmt(v)}</td>`).join('')}
      </tr>`
    : ''

  // Dentist Fee Summary
  const dfHeaders = [
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NO'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DOCTOR'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NEW_PATIENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.EXISTING_PATIENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TOTAL_TREATMENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.HAND_INSURANCE'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TOTAL_PAID'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DF_TREATMENT_RIGHT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TOTAL_DF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.LAB_FEE'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DF_LAB_FEE'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NET_DF')
  ]
  const dfHeaderHtml = dfHeaders.map((h) => `<th>${escapeHtml(h)}</th>`).join('')
  const dfBodyHtml = rowDf
    .map((row: any, i: number) => {
      const cells = [
        i + 1,
        doctorName(row.doctorId),
        fmt(row.newPatientCount, 0),
        fmt(row.existingPatientCount, 0),
        fmt(row.totalAmount),
        fmt(row.handInsurance),
        fmt(row.amountPaid),
        fmt(row.amountCf),
        fmt(row.amountDf),
        fmt(row.dfCredit),
        fmt(row.dfLostTreatment),
        fmt(row.dfLostCreditAndTreatment),
        fmt(row.dfLabFee),
        fmt(row.dfLabFee),
        fmt(row.dfNet)
      ]
      const cellHtml = cells
        .map((c, idx) => `<td class="${idx >= 2 ? 'num' : ''}">${escapeHtml(c)}</td>`)
        .join('')
      return `<tr>${cellHtml}</tr>`
    })
    .join('')

  // Income Summary
  const incomeHeaders = [
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.INCOME'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NET_CLINIC'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.NET_DF'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DENTIST_FEE'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DF_LAB'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CASH'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TRANSFER'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.DEBIT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CREDIT_PERCENT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.CREDIT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.TREATMENT_RIGHT'),
    rpt.t('REPORT.TABLE.REMUNERATION_DCC.ADVANCE')
  ]
  const incomeHeaderHtml = incomeHeaders.map((h) => `<th>${escapeHtml(h)}</th>`).join('')
  const incomeBodyHtml = summaryIncome
    ? `<tr>
        <td class="num">${fmt(summaryIncome.income)}</td>
        <td class="num">${fmt(summaryIncome.netClinic)}</td>
        <td class="num">${fmt(summaryIncome.netDf)}</td>
        <td class="num">${fmt(summaryIncome.dentistFee)}</td>
        <td class="num">${fmt(summaryIncome.dfLab)}</td>
        <td class="num">${fmt(summaryIncome.amountCash)}</td>
        <td class="num">${fmt(summaryIncome.amountTransfer)}</td>
        <td class="num">${fmt(summaryIncome.amountDebit)}</td>
        <td class="num">${fmt(summaryIncome.creditCost)}</td>
        <td class="num">${fmt(summaryIncome.amountCredit)}</td>
        <td class="num">${fmt(summaryIncome.amountTreatmentRight)}</td>
        <td class="num">${fmt(summaryIncome.amountAdvance)}</td>
      </tr>`
    : ''

  const filterHtml = filterRows
    .map(
      ([label, value]) =>
        `<div class="filter-item"><span class="filter-label">${escapeHtml(label)}:</span> <span>${escapeHtml(value)}</span></div>`
    )
    .join('')

  const html = `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    font-family: 'Sarabun', 'Sukhumvit Set', 'Thonburi', 'Helvetica', 'Arial', sans-serif;
    color: #000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body { padding: 8px; font-size: 9px; }
  h1 { font-size: 14px; margin: 0 0 8px 0; }
  h2 { font-size: 11px; margin: 12px 0 6px 0; }
  .filter-block { display: flex; flex-wrap: wrap; gap: 4px 14px; margin-bottom: 8px; }
  .filter-item { font-size: 9px; }
  .filter-label { font-weight: 700; }
  table { width: 100%; border-collapse: collapse; table-layout: auto; margin-bottom: 8px; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; }
  th, td {
    border: 1px solid #555;
    padding: 3px 4px;
    font-size: 8.5px;
    vertical-align: top;
    word-break: break-word;
  }
  th {
    background: #E5E5E5;
    font-weight: 700;
    text-align: center;
  }
  td.num { text-align: right; white-space: nowrap; }
  tr.row-sum td { font-weight: 700; background: #F2F2F2; }
  tr.row-sum td.sum-label { text-align: right; }
  .empty { text-align: center; padding: 12px; color: #666; }
  @media print {
    .no-print { display: none !important; }
  }
  .toolbar {
    position: fixed; top: 8px; right: 8px; z-index: 10;
    background: #fff; padding: 6px 8px; border: 1px solid #ccc; border-radius: 6px;
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  }
  .toolbar button {
    font-size: 12px; padding: 4px 10px; cursor: pointer;
    border: 1px solid #888; background: #f5f5f5; border-radius: 4px;
  }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Print / Save as PDF</button>
  </div>

  <h1>${escapeHtml(title)}</h1>
  <div class="filter-block">${filterHtml}</div>

  <h2>${escapeHtml(rpt.t('REPORT.TABLE.DOCTOR_FEE') || 'Doctor Fee')}</h2>
  <table>
    <thead><tr>${mainHeaderHtml}</tr></thead>
    <tbody>
      ${mainBodyHtml || `<tr><td class="empty" colspan="${mainHeaders.length}">${escapeHtml(rpt.t('REPORT.TITLE.NO_DATA') || 'No data')}</td></tr>`}
      ${mainSumRowHtml}
    </tbody>
  </table>

  ${rowDf.length ? `
  <h2>${escapeHtml(rpt.t('REPORT.TITLE.DENTIST_FEE_SUMMARY') || 'Dentist Fee Summary')}</h2>
  <table>
    <thead><tr>${dfHeaderHtml}</tr></thead>
    <tbody>${dfBodyHtml}</tbody>
  </table>` : ''}

  ${summaryIncome ? `
  <h2>${escapeHtml(rpt.t('REPORT.TITLE.INCOME_SUMMARY') || 'Income Summary')}</h2>
  <table>
    <thead><tr>${incomeHeaderHtml}</tr></thead>
    <tbody>${incomeBodyHtml}</tbody>
  </table>` : ''}

<script>
  (function() {
    var triggered = false;
    function go() {
      if (triggered) return;
      triggered = true;
      try { window.focus(); window.print(); } catch (e) {}
    }
    if (document.readyState === 'complete') {
      setTimeout(go, 250);
    } else {
      window.addEventListener('load', function() { setTimeout(go, 250); });
    }
  })();
${'</' + 'script>'}
</body>
</html>`

  const win = window.open('', '_blank')
  if (!win) {
    alert('Please allow pop-ups to export the PDF')
    return
  }
  win.document.open()
  win.document.write(html)
  win.document.close()
}
